import { useTranslate } from "@opensea/next-translate"
import { FlexColumn, CenterAligned, Spinner } from "@opensea/ui-kit"
import { AuthCode } from "@opensea/ui-kit/client"
import { useMfaEnrollment } from "@privy-io/react-auth"
import { QRCodeSVG } from "qrcode.react"
import React, { useState } from "react"
import { captureException } from "@sentry/nextjs"
import { TextCopierButton } from "@/components/common/TextCopierButton.react"
import { Modal } from "@/design-system/Modal"
import { Text } from "@/design-system/Text"
import { useMountEffect } from "@/hooks/useMountEffect"
import { useGlobalModal } from "@/providers/GlobalModal"

type Props = {
  onClose: () => void
}

export function ManageTotpModal({ onClose }: Props) {
  const t = useTranslate("mfa")
  const { closeModal, onPrevious } = useGlobalModal()
  const { initEnrollmentWithTotp, submitEnrollmentWithTotp } =
    useMfaEnrollment()
  const [totpData, setTotpData] = useState<{
    authUrl: string
    secret: string
  }>()
  const [error, setError] = useState(false)

  useMountEffect(() => {
    const getTotpData = async () => {
      try {
        const { authUrl, secret } = await initEnrollmentWithTotp()
        setTotpData({ authUrl, secret })
      } catch (e) {
        captureException(e, { tags: { userFlow: "privy" } })
        setError(true)
      }
    }
    void getTotpData()
  })

  const handleAuthCode = async (mfaCode: string) => {
    setError(false)
    try {
      await submitEnrollmentWithTotp({ mfaCode })
      // we only open this from MfaMethodsModal, so we should always have onPrevious
      onPrevious?.() ?? closeModal()
    } catch (e) {
      setError(true)
    }
  }

  return (
    <>
      <Modal.Header
        onClose={onClose}
        title={t("enrollment.totp.modal.header", "Set up authenticator app")}
      />
      <Modal.Body className="flex flex-1 flex-col py-4">
        <FlexColumn className="items-center gap-8 text-center">
          <Text.Heading size="medium">
            {t(
              "enrollment.totp.scanCode",
              "Scan QR code using an authenticator app",
            )}
          </Text.Heading>
          {totpData ? (
            <FlexColumn>
              <CenterAligned className="self-center rounded-xl bg-white p-3">
                <QRCodeSVG size={140} value={totpData.authUrl} />
              </CenterAligned>
              <TextCopierButton
                className="bg-transparent p-3 font-normal hover:bg-transparent"
                text={totpData.secret}
                variant="secondary"
              >
                <Text color="secondary" size="tiny">
                  {t(
                    "enrollment.totp.copyKeyCode",
                    "Can't scan? Copy the key:",
                  )}
                  <div>{totpData.secret}</div>
                </Text>
              </TextCopierButton>
            </FlexColumn>
          ) : (
            <CenterAligned className="size-[140px] self-center rounded-xl p-3">
              <Spinner />
            </CenterAligned>
          )}
          <AuthCode
            error={error}
            onComplete={handleAuthCode}
            overrides={{
              Inputs: {
                className: "bg-component-gray-1 dark:bg-gray-3",
              },
            }}
          />
        </FlexColumn>
      </Modal.Body>
    </>
  )
}
